/* Rebound sections for Dawn: product page, gallery, carousels. */
(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const value = (Number(cents) || 0) / 100;
    const group = (decimals, thousands, decimal) => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    let amount;
    switch (key) {
      case 'amount_no_decimals': amount = group(0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = group(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = group(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = group(2, "'", '.'); break;
      default: amount = group(2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  /* ---------- Gallery ---------- */
  if (!customElements.get('rb-gallery')) {
    customElements.define('rb-gallery', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rb-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.counter = this.querySelector('[data-rb-counter]');
        this.thumbs = Array.from(this.querySelectorAll('[data-rb-thumb]'));
        this.querySelector('[data-rb-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
        this.querySelector('[data-rb-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.rbThumb))));
        this.index = 0;
        let ticking = false;
        this.track.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            if (i !== this.index) this.setActive(i);
          });
        }, { passive: true });
      }

      goTo(i, smooth = true) {
        const count = this.slides.length;
        if (!count) return;
        const target = (i + count) % count;
        this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        this.setActive(target);
      }

      goToMedia(mediaId) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i > -1) this.goTo(i);
      }

      setActive(i) {
        this.index = i;
        if (this.counter) this.counter.textContent = i + 1;
        this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
        this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.slides.forEach((s, n) => { if (n !== i) s.querySelectorAll('video').forEach((v) => v.pause()); });
      }
    });
  }

  /* ---------- Horizontal carousel (reviews, photo strips) ---------- */
  if (!customElements.get('rb-carousel')) {
    customElements.define('rb-carousel', class extends HTMLElement {
      connectedCallback() {
        const track = this.querySelector('[data-rb-track]');
        if (!track) return;
        const step = () => Math.max(track.clientWidth * 0.8, 200);
        this.querySelector('[data-rb-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
        this.querySelector('[data-rb-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      }
    });
  }

  /* ---------- Product ---------- */
  if (!customElements.get('rb-product')) {
    customElements.define('rb-product', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.form) return;
        this.idInput = this.form.querySelector('[data-rb-variant-id]');
        this.qtyInput = this.form.querySelector('[data-rb-quantity]');
        this.moneyFormat = this.dataset.moneyFormat;
        this.submit = this.querySelector('[data-rb-submit]');
        this.gallery = this.querySelector('rb-gallery');

        const json = this.querySelector('[data-rb-variants]');
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.variant = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];

        this.querySelectorAll('[data-rb-option]').forEach((el) => el.addEventListener('change', () => this.onOptionChange()));

        this.tiers = Array.from(this.querySelectorAll('[data-rb-tier]'));
        if (this.tiers.length && !this.tiers.some((t) => t.checked)) this.tiers[0].checked = true;
        this.tiers.forEach((t) => t.addEventListener('change', () => this.update()));

        this.initStepper();
        this.initDelivery();
        this.initSticky();
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
        this.update();
      }

      money(cents) { return formatMoney(cents, this.moneyFormat); }

      selectedTier() { return this.tiers.find((t) => t.checked); }

      quantity() {
        const tier = this.selectedTier();
        if (tier) return Math.max(1, parseInt(tier.dataset.qty, 10) || 1);
        const input = this.querySelector('[data-rb-qty-input]');
        return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
      }

      onOptionChange() {
        const selected = [];
        this.querySelectorAll('.rb-variants__option').forEach((fieldset, i) => {
          const checked = fieldset.querySelector('input[data-rb-option]:checked');
          const select = fieldset.querySelector('select[data-rb-option]');
          selected[i] = checked ? checked.value : select ? select.value : null;
          const label = this.querySelector(`[data-rb-option-label="${i}"]`);
          if (label) label.textContent = selected[i];
        });
        const match = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        this.variant = match || null;
        if (match) {
          this.idInput.value = match.id;
          if (match.featured_media && this.gallery) this.gallery.goToMedia(match.featured_media.id);
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        }
        this.update();
      }

      update() {
        const v = this.variant;
        const price = v ? v.price : 0;

        const priceEl = this.querySelector('[data-rb-price]');
        const compareEl = this.querySelector('[data-rb-compare]');
        if (v && priceEl) priceEl.textContent = this.money(price);
        if (compareEl) {
          const show = v && v.compare_at_price > v.price;
          compareEl.hidden = !show;
          if (show) compareEl.textContent = this.money(v.compare_at_price);
        }

        this.tiers.forEach((t) => {
          const qty = parseInt(t.dataset.qty, 10) || 1;
          const disc = parseInt(t.dataset.discount, 10) || 0;
          const full = price * qty;
          const total = Math.floor((full * (100 - disc)) / 100);
          const pack = t.closest('[data-rb-pack]');
          const p = pack.querySelector('[data-rb-tier-price]');
          const c = pack.querySelector('[data-rb-tier-compare]');
          const s = pack.querySelector('[data-rb-tier-save]');
          if (p) p.textContent = this.money(total);
          if (c) { c.textContent = this.money(full); c.hidden = disc === 0; }
          if (s) s.textContent = this.money(full - total);
        });

        const qty = this.quantity();
        this.qtyInput.value = qty;

        if (this.submit) {
          const text = this.submit.querySelector('[data-rb-submit-text]');
          const available = v && v.available;
          this.submit.disabled = !available;
          text.textContent = !v ? this.submit.dataset.labelUnavailable
            : available ? this.submit.dataset.labelAdd : this.submit.dataset.labelSoldout;
        }

        if (this.sticky) {
          const tier = this.selectedTier();
          let title = this.dataset.productTitle;
          if (tier && tier.dataset.title) title = tier.dataset.title;
          else if (v && v.title !== 'Default Title') title = `${title} – ${v.title}`;
          this.sticky.querySelector('[data-rb-sticky-title]').textContent = title;
          let total = price * qty;
          if (tier) total = Math.floor((total * (100 - (parseInt(tier.dataset.discount, 10) || 0))) / 100);
          this.sticky.querySelector('[data-rb-sticky-price]').textContent = this.money(total);
          this.stickyButton.disabled = !(v && v.available);
        }
      }

      initStepper() {
        const input = this.querySelector('[data-rb-qty-input]');
        if (!input) return;
        const set = (n) => { input.value = Math.max(1, n); this.update(); };
        this.querySelector('[data-rb-qty-minus]')?.addEventListener('click', () => set((parseInt(input.value, 10) || 1) - 1));
        this.querySelector('[data-rb-qty-plus]')?.addEventListener('click', () => set((parseInt(input.value, 10) || 1) + 1));
        input.addEventListener('change', () => set(parseInt(input.value, 10) || 1));
      }

      initDelivery() {
        const el = this.querySelector('[data-rb-delivery]');
        if (!el) return;
        const skip = el.dataset.skipWeekends === 'true';
        const addDays = (n) => {
          const d = new Date();
          let added = 0;
          while (added < n) {
            d.setDate(d.getDate() + 1);
            if (!skip || (d.getDay() !== 0 && d.getDay() !== 6)) added++;
          }
          return d;
        };
        const min = parseInt(el.dataset.min, 10) || 1;
        const max = Math.max(min, parseInt(el.dataset.max, 10) || min);
        const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
        el.querySelector('[data-rb-delivery-dates]').textContent = `${fmt.format(addDays(min))} – ${fmt.format(addDays(max))}`;
        el.hidden = false;
      }

      initSticky() {
        this.sticky = this.querySelector('[data-rb-sticky]');
        if (!this.sticky) return;
        if (!this.submit) { this.sticky.remove(); this.sticky = null; return; }
        this.stickyButton = this.sticky.querySelector('[data-rb-sticky-button]');
        this.stickyButton.addEventListener('click', () => this.form.requestSubmit(this.submit));
        new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', String(!show));
          this.stickyButton.tabIndex = show ? 0 : -1;
        }).observe(this.submit);
      }

      showError(message) {
        const el = this.querySelector('[data-rb-error]');
        if (!el) return;
        el.textContent = message || '';
        el.hidden = !message;
      }

      onSubmit(e) {
        e.preventDefault();
        if (this.busy || (this.submit && this.submit.disabled)) return;
        this.update();
        this.showError(false);

        // Uses Dawn's cart drawer / notification when present, otherwise goes to the cart page.
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (!cart || typeof cart.renderContents !== 'function') {
          this.form.submit();
          return;
        }

        const body = new FormData(this.form);
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

        this.busy = true;
        this.submit?.classList.add('loading');
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        })
          .then((r) => r.json())
          .then((res) => {
            if (res.status) {
              this.showError(res.description || res.message);
              return;
            }
            // Dawn declares these as top-level globals (not window properties).
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: body.get('id'), cartData: res });
            }
            cart.classList.remove('is-empty');
            cart.renderContents(res);
          })
          .catch(() => { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; })
          .finally(() => {
            this.busy = false;
            this.submit?.classList.remove('loading');
          });
      }
    });
  }

  /* ---------- "Show more" buttons (reviews on mobile) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rb-show-more]');
    if (!btn) return;
    btn.closest('[data-rb-more-scope]')?.classList.add('is-expanded');
    btn.remove();
  });
})();